// =============================================================================
// Hook: useReportQueue
// Manages report generation via the queue system with optional notifications
// =============================================================================

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProject } from "./useProject";
import { toast } from "sonner";
import { notifyReportReady, notifyBatchComplete } from "@/utils/notifications";

// =============================================================================
// Types
// =============================================================================

export type JobStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
export type JobPriority = 1 | 2; // 1 = manual (high), 2 = auto (low)

export interface ReportJob {
  id: number;
  submission_id: number;
  template_id: number;
  project_id: number;
  priority: JobPriority;
  status: JobStatus;
  attempts: number;
  max_attempts: number;
  error_message: string | null;
  notify_recipient: boolean;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  created_by: string | null;
}

export interface GeneratedReport {
  id: number;
  submission_id: number;
  template_id: number;
  project_id: number;
  version: number;
  file_path: string;
  file_name: string;
  file_size_bytes: number | null;
  generation_time_ms: number | null;
  status: string;
  is_outdated: boolean;
  created_at: string;
}

export interface EnqueueReportParams {
  submissionId: number;
  templateId: number;
  priority?: JobPriority;
  notifyRecipient?: boolean;
}

export interface EnqueueBatchParams {
  submissionIds: number[];
  templateId: number;
  priority?: JobPriority;
  notifyRecipient?: boolean;
}

interface ManageReportResponse {
  success: boolean;
  job?: ReportJob;
  jobs?: ReportJob[];
  report?: GeneratedReport;
  reports?: GeneratedReport[];
  downloadUrl?: string;
  error?: string;
  message?: string;
}

// =============================================================================
// Query Keys
// =============================================================================

const QUERY_KEYS = {
  jobs: (projectId: number) => ["report-jobs", projectId],
  jobsBySubmission: (submissionId: number) => ["report-jobs", "submission", submissionId],
  reports: (submissionId: number) => ["generated-reports", submissionId],
  latestReport: (submissionId: number, templateId: number) =>
    ["generated-report", "latest", submissionId, templateId],
};

// =============================================================================
// Hook
// =============================================================================

export function useReportQueue() {
  const { projectId } = useProject();
  const queryClient = useQueryClient();

  // ===========================================================================
  // Helper: Call manage-report edge function
  // ===========================================================================

  const callManageReport = async (
    params: Record<string, unknown>
  ): Promise<ManageReportResponse> => {
    const { data, error } = await supabase.functions.invoke("manage-report", {
      body: params,
    });

    if (error) throw error;
    if (!data.success) throw new Error(data.error || "Operation failed");
    return data;
  };

  // ===========================================================================
  // Query: Get all jobs for current project (from pgmq)
  // ===========================================================================

  const jobsQuery = useQuery({
    queryKey: QUERY_KEYS.jobs(projectId),
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_project_report_jobs", {
        p_project_id: projectId,
      });

      if (error) throw error;
      return (data || []) as ReportJob[];
    },
    enabled: !!projectId,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });

  // ===========================================================================
  // Query: Get jobs for a specific submission (from pgmq)
  // ===========================================================================

  const useJobsBySubmission = (submissionId: number) => {
    return useQuery({
      queryKey: QUERY_KEYS.jobsBySubmission(submissionId),
      queryFn: async () => {
        const { data, error } = await supabase.rpc("get_active_report_jobs", {
          p_submission_id: submissionId,
        });

        if (error) throw error;
        return (data || []) as ReportJob[];
      },
      enabled: !!submissionId,
      staleTime: 10 * 1000,
      refetchInterval: 30 * 1000,
    });
  };

  // ===========================================================================
  // Query: Get generated reports for a submission
  // ===========================================================================

  const useReportsBySubmission = (submissionId: number) => {
    return useQuery({
      queryKey: QUERY_KEYS.reports(submissionId),
      queryFn: async () => {
        const { data, error } = await supabase
          .from("generated_reports")
          .select("*")
          .eq("submission_id", submissionId)
          .order("created_at", { ascending: false });

        if (error) throw error;
        return data as GeneratedReport[];
      },
      enabled: !!submissionId,
      staleTime: 30 * 1000,
    });
  };

  // ===========================================================================
  // Query: Get latest report for submission/template
  // ===========================================================================

  const useLatestReport = (submissionId: number, templateId: number) => {
    return useQuery({
      queryKey: QUERY_KEYS.latestReport(submissionId, templateId),
      queryFn: async () => {
        const { data, error } = await supabase
          .from("generated_reports")
          .select("*")
          .eq("submission_id", submissionId)
          .eq("template_id", templateId)
          .eq("status", "completed")
          .eq("is_outdated", false)
          .order("version", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) throw error;
        return data as GeneratedReport | null;
      },
      enabled: !!submissionId && !!templateId,
      staleTime: 30 * 1000,
    });
  };

  // ===========================================================================
  // Mutation: Enqueue single report
  // ===========================================================================

  const enqueueReportMutation = useMutation({
    mutationFn: async (params: EnqueueReportParams) => {
      return callManageReport({
        submissionId: params.submissionId,
        templateId: params.templateId,
        projectId: projectId,
        priority: params.priority || 1,
        notifyRecipient: params.notifyRecipient || false,
      });
    },
    onSuccess: (result) => {
      toast.success("Reporte encolado para generación");
      queryClient.invalidateQueries({ queryKey: ["report-jobs"] });
    },
    onError: (error) => {
      console.error("Error enqueueing report:", error);
      toast.error(`Error al encolar reporte: ${error.message}`);
    },
  });

  // ===========================================================================
  // Mutation: Enqueue batch of reports
  // ===========================================================================

  const enqueueBatchMutation = useMutation({
    mutationFn: async (params: EnqueueBatchParams) => {
      // Jobs only contain submission/template/project IDs
      const jobs = params.submissionIds.map((submissionId) => ({
        submissionId: submissionId,
        templateId: params.templateId,
        projectId: projectId,
      }));

      // Call batch endpoint - priority and notifyRecipient go at root level
      const { data, error } = await supabase.functions.invoke("manage-report/batch", {
        body: {
          jobs,
          priority: params.priority || 2,
          notifyRecipient: params.notifyRecipient || false,
        },
      });
      if (error) throw error;
      if (!data.success) throw new Error(data.error || "Batch operation failed");
      return data as ManageReportResponse;
    },
    onSuccess: async (result) => {
      const count = result.jobs?.length || 0;
      toast.success(`${count} reportes encolados para generación`);
      queryClient.invalidateQueries({ queryKey: ["report-jobs"] });
      // Play notification sound when batch is queued
      await notifyBatchComplete(count, 0);
    },
    onError: (error) => {
      console.error("Error enqueueing batch:", error);
      toast.error(`Error al encolar reportes: ${error.message}`);
    },
  });

  // ===========================================================================
  // Mutation: Cancel job (DELETE /manage-report/:jobId)
  // ===========================================================================

  const cancelJobMutation = useMutation({
    mutationFn: async (jobId: number) => {
      const { data, error } = await supabase.functions.invoke(`manage-report/${jobId}`, {
        method: "DELETE",
      });
      if (error) throw error;
      if (!data.success) throw new Error(data.error || "Cancel failed");
      return data as ManageReportResponse;
    },
    onSuccess: () => {
      toast.success("Job cancelado");
      queryClient.invalidateQueries({ queryKey: ["report-jobs"] });
    },
    onError: (error) => {
      toast.error(`Error cancelando job: ${error.message}`);
    },
  });

  // ===========================================================================
  // Mutation: Retry failed job (re-enqueue from archived message)
  // With pgmq, failed jobs are archived - we need to create a new message
  // ===========================================================================

  const retryJobMutation = useMutation({
    mutationFn: async (jobId: number) => {
      // Get the archived message to extract submission/template info
      const { data: archivedJob, error: fetchError } = await supabase
        .from("pgmq.a_reports" as any)
        .select("message")
        .eq("msg_id", jobId)
        .single();

      if (fetchError || !archivedJob) {
        throw new Error("Job archivado no encontrado");
      }

      const message = archivedJob.message as {
        submission_id: number;
        template_id: number;
        project_id: number;
        priority: number;
        notify_recipient: boolean;
      };

      // Re-enqueue the job
      const { data: newMsgId, error: enqueueError } = await supabase.rpc(
        "send_report_to_queue",
        {
          p_message: {
            ...message,
            created_at: new Date().toISOString(),
          },
          p_delay: 0,
        }
      );

      if (enqueueError) throw enqueueError;
      return { success: true, newJobId: newMsgId };
    },
    onSuccess: () => {
      toast.success("Job re-encolado para reintento");
      queryClient.invalidateQueries({ queryKey: ["report-jobs"] });
    },
    onError: (error) => {
      toast.error(`Error reintentando job: ${error.message}`);
    },
  });

  // ===========================================================================
  // Mutation: Trigger queue processing manually
  // ===========================================================================

  const processQueueMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke(
        "process-report-queue",
        { body: {} }
      );

      if (error) throw error;
      return data;
    },
    onSuccess: async (result) => {
      if (!result.processed) {
        toast.info("No hay reportes pendientes");
      } else if (result.success) {
        toast.success("Reporte procesado correctamente");
        // Play notification sound
        await notifyReportReady("Reporte", { browserNotification: true });
      } else {
        toast.error(`Error procesando: ${result.error}`);
      }
      queryClient.invalidateQueries({ queryKey: ["report-jobs"] });
      queryClient.invalidateQueries({ queryKey: ["generated-reports"] });
    },
    onError: (error) => {
      toast.error(`Error procesando cola: ${error.message}`);
    },
  });

  // ===========================================================================
  // Mutation: Send notification for existing report
  // ===========================================================================

  const sendNotificationMutation = useMutation({
    mutationFn: async (reportId: number) => {
      const { data, error } = await supabase.functions.invoke("manage-report/notify", {
        body: { reportId },
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error || "Notification failed");
      return data;
    },
    onSuccess: () => {
      toast.success("Email encolado para envío");
    },
    onError: (error) => {
      toast.error(`Error enviando notificación: ${error.message}`);
    },
  });

  // ===========================================================================
  // Helpers
  // ===========================================================================

  const getJobStats = () => {
    const jobs = jobsQuery.data || [];
    return {
      total: jobs.length,
      pending: jobs.filter((j) => j.status === "pending").length,
      processing: jobs.filter((j) => j.status === "processing").length,
      completed: jobs.filter((j) => j.status === "completed").length,
      failed: jobs.filter((j) => j.status === "failed").length,
    };
  };

  const getJobForSubmission = (submissionId: number, templateId: number): ReportJob | undefined => {
    const jobs = jobsQuery.data || [];
    return jobs.find(
      (j) =>
        j.submission_id === submissionId &&
        j.template_id === templateId &&
        (j.status === "pending" || j.status === "processing")
    );
  };

  const hasActiveJob = (submissionId: number, templateId: number): boolean => {
    return !!getJobForSubmission(submissionId, templateId);
  };

  // ===========================================================================
  // Download helper
  // ===========================================================================

  const downloadReport = async (reportId: number, fileName?: string) => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const downloadUrl = `${supabaseUrl}/functions/v1/download-report/${reportId}?source=admin`;

      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(downloadUrl, {
        headers: session?.access_token
          ? { Authorization: `Bearer ${session.access_token}` }
          : {},
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || `report_${reportId}.pptx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success("Reporte descargado");
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      toast.error(`Error descargando: ${message}`);
      throw error;
    }
  };

  // ===========================================================================
  // Return
  // ===========================================================================

  return {
    // Data
    jobs: jobsQuery.data || [],
    isLoading: jobsQuery.isLoading,
    error: jobsQuery.error,
    refetch: jobsQuery.refetch,

    // Hooks for specific queries
    useJobsBySubmission,
    useReportsBySubmission,
    useLatestReport,

    // Actions
    enqueueReport: enqueueReportMutation.mutate,
    enqueueReportAsync: enqueueReportMutation.mutateAsync,
    enqueueBatch: enqueueBatchMutation.mutate,
    enqueueBatchAsync: enqueueBatchMutation.mutateAsync,
    cancelJob: cancelJobMutation.mutate,
    cancelJobAsync: cancelJobMutation.mutateAsync,
    retryJob: retryJobMutation.mutate,
    retryJobAsync: retryJobMutation.mutateAsync,
    processQueue: processQueueMutation.mutate,
    processQueueAsync: processQueueMutation.mutateAsync,
    sendNotification: sendNotificationMutation.mutate,
    sendNotificationAsync: sendNotificationMutation.mutateAsync,
    downloadReport,

    // Loading states
    isEnqueuing: enqueueReportMutation.isPending,
    isEnqueuingBatch: enqueueBatchMutation.isPending,
    isCancelling: cancelJobMutation.isPending,
    isRetrying: retryJobMutation.isPending,
    isProcessing: processQueueMutation.isPending,
    isSendingNotification: sendNotificationMutation.isPending,

    // Helpers
    getJobStats,
    getJobForSubmission,
    hasActiveJob,
  };
}

// =============================================================================
// Hook: useSubmissionReportStatus
// Convenience hook for checking report status of a single submission
// =============================================================================

export function useSubmissionReportStatus(submissionId: number, templateId: number) {
  const { useJobsBySubmission, useLatestReport, hasActiveJob } = useReportQueue();

  const jobsQuery = useJobsBySubmission(submissionId);
  const reportQuery = useLatestReport(submissionId, templateId);

  const activeJob = jobsQuery.data?.find(
    (j) => j.template_id === templateId && (j.status === "pending" || j.status === "processing")
  );

  const latestFailedJob = jobsQuery.data?.find(
    (j) => j.template_id === templateId && j.status === "failed"
  );

  return {
    // Status
    isGenerating: !!activeJob,
    hasReport: !!reportQuery.data,
    hasFailed: !!latestFailedJob && !activeJob,

    // Data
    activeJob,
    latestFailedJob,
    report: reportQuery.data,

    // Loading
    isLoading: jobsQuery.isLoading || reportQuery.isLoading,

    // Refresh
    refetch: () => {
      jobsQuery.refetch();
      reportQuery.refetch();
    },
  };
}
